import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logAdminAction } from "@/admin/services/adminService";
import { useAdminAuth } from "./useAdminAuth";
import type { AdminAnnonceRow, ModerationStatus } from "@/admin/types/admin";

export function useAdsModeration(statusFilter?: ModerationStatus) {
  const queryClient = useQueryClient();
  const { adminUserId } = useAdminAuth();

  const query = useQuery({
    queryKey: ["admin-ads", statusFilter],
    queryFn: async (): Promise<AdminAnnonceRow[]> => {
      let q = supabase
        .from("annonces")
        .select("id, title, user_id, wilaya, commune, metier, status, moderation_status, removed_at, remove_reason_code, moderation_notes, created_at")
        .order("created_at", { ascending: false });

      if (statusFilter) {
        q = q.eq("moderation_status", statusFilter);
      }

      const { data, error } = await q;
      if (error) throw error;
      const rows = data ?? [];

      // Résolution des noms auteurs en batch
      const userIds = [...new Set(rows.map((r: any) => r.user_id).filter(Boolean))];
      let nameMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, display_name, first_name, last_name")
          .in("user_id", userIds);
        (profiles ?? []).forEach((p: any) => {
          nameMap[p.user_id] =
            p.display_name ||
            `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() ||
            "Inconnu";
        });
      }

      return rows.map((row: any) => ({
        id: row.id,
        title: row.title,
        userId: row.user_id,
        authorName: nameMap[row.user_id] ?? "Inconnu",
        wilaya: row.wilaya,
        commune: row.commune,
        metier: row.metier,
        status: row.status,
        moderationStatus: (row.moderation_status ?? "active") as ModerationStatus,
        removedAt: row.removed_at ?? null,
        removeReasonCode: row.remove_reason_code ?? null,
        moderationNotes: row.moderation_notes ?? null,
        createdAt: row.created_at,
      }));
    },
  });

  const hideMutation = useMutation({
    mutationFn: async ({
      annonceId,
      reasonCode,
      notes,
    }: {
      annonceId: string;
      reasonCode: string;
      notes?: string;
    }) => {
      const { error } = await supabase
        .from("annonces")
        .update({
          moderation_status: "hidden",
          moderation_notes: notes ?? null,
        })
        .eq("id", annonceId);
      if (error) throw error;
      return { annonceId, reasonCode, notes };
    },
    onSuccess: async ({ annonceId, reasonCode, notes }) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: "ad_hidden",
          targetType: "annonce",
          targetId: annonceId,
          reasonCode,
          notes,
          afterState: { moderation_status: "hidden" },
        });
      }
      toast.success("Annonce masquée");
      queryClient.invalidateQueries({ queryKey: ["admin-ads"] });
    },
    onError: () => toast.error("Erreur lors du masquage"),
  });

  const removeMutation = useMutation({
    mutationFn: async ({
      annonceId,
      reasonCode,
      notes,
    }: {
      annonceId: string;
      reasonCode: string;
      notes?: string;
    }) => {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("annonces")
        .update({
          moderation_status: "removed",
          removed_at: now,
          removed_by_admin_id: adminUserId,
          remove_reason_code: reasonCode,
          moderation_notes: notes ?? null,
        })
        .eq("id", annonceId);
      if (error) throw error;
      return { annonceId, reasonCode, notes };
    },
    onSuccess: async ({ annonceId, reasonCode, notes }) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: "ad_removed",
          targetType: "annonce",
          targetId: annonceId,
          reasonCode,
          notes,
          afterState: { moderation_status: "removed" },
        });
      }
      toast.success("Annonce supprimée");
      queryClient.invalidateQueries({ queryKey: ["admin-ads"] });
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });

  const markSafeMutation = useMutation({
    mutationFn: async (annonceId: string) => {
      const { error } = await supabase
        .from("annonces")
        .update({
          moderation_status: "active",
          moderation_notes: null,
        })
        .eq("id", annonceId);
      if (error) throw error;
      return annonceId;
    },
    onSuccess: async (annonceId) => {
      if (adminUserId) {
        await logAdminAction({
          adminUserId,
          actionType: "ad_marked_safe",
          targetType: "annonce",
          targetId: annonceId,
          afterState: { moderation_status: "active" },
        });
      }
      toast.success("Annonce marquée comme conforme");
      queryClient.invalidateQueries({ queryKey: ["admin-ads"] });
    },
    onError: () => toast.error("Erreur lors de la mise à jour"),
  });

  return { query, hideMutation, removeMutation, markSafeMutation };
}
